import { useMemo } from 'react';
import { getAfaRate, getAfaOn } from './useRscPricing';
import { suggestInverterUpgrade } from '../utils/rscSizing';

// Solar yield — commercial sites derate from RSC 3.5.
const YIELD_PER_KWP = 3.4;        // kWh per kWp per day, MY commercial
const DAYS_PER_MONTH = 30;
const WORKWEEKS_PER_MONTH = 4.345;
const SMP_RATE = 0.20;            // RM/kWh sellback
const PANEL_KWP = 0.65;           // 650Wp per panel — MUST match the card's panel watt
                                  // (default 650). Was 0.64: sizing fit panels for 640W
                                  // then the card recomputed kWp at 650W, pushing the ratio
                                  // ~1.5% over the 1.45 cap (e.g. 161×650=104.65 on 72kW =
                                  // 1.4535). 0.65 keeps sizing == display; smaller 620/640
                                  // panels only ever land UNDER the cap. (user 2026-08-08)

// Safety ratio — kWp never exceeds inverter × 1.45 (battery or not). CSC commercial cap
// raised 1.42 → 1.45 (user 2026-08-04) to match the residential no-battery cap.
export const RATIO_CAP_NO_BATT = 1.45;

// Solar generation window — daily kWh × 3.4 is assumed to be produced
// flat across this 10-hour band. Hours outside the band have no generation.
// Solar window widened to 07:00–20:00 with the empirical Malaysian generation shape (from the
// Astern BESS simulator) — captures the dawn/dusk shoulders a bell-over-8-18 misses. Daily total
// still = kWp × YIELD_PER_KWP (3.4, ours); the shape sums to 4 so ∫(shape×dailyGen)dt @0.25h step
// = dailyGen. Peak stays ~0.146×dailyGen (≈ our old half-sine peak). (user 2026-07-24)
const SOLAR_START_HR = 7;
const SOLAR_END_HR = 20;
const SOLAR_HOURS_PER_DAY = SOLAR_END_HR - SOLAR_START_HR;   // 13
const SOLAR_SHAPE = [0.00008,0.00097,0.00391,0.00887,0.01477,0.02351,0.03471,0.0436,0.05491,0.06512,0.07478,0.08246,0.09122,0.09897,0.10939,0.1132,0.12404,0.12261,0.12957,0.12976,0.12902,0.12746,0.12908,0.13352,0.13923,0.13858,0.14562,0.14585,0.14201,0.13171,0.12799,0.12536,0.11563,0.11645,0.1057,0.09836,0.09674,0.08702,0.08046,0.07321,0.06348,0.05632,0.04621,0.03661,0.02898,0.0212,0.01536,0.00956,0.00502,0.00111,0.0004,0.00027,0.00002];  // 07:00–20:00, 15-min, Σ≈4

// ── TNB commercial LV tariff: "Bukan Domestik Am Voltan Rendah" ──────────────
// New tariff structure effective 1 July 2025, verified line-for-line against a
// real May-2026 bill (210492234600): 9,599 kWh →
//   Energy   0.2703/kWh = 2,594.61   Capacity 0.0883/kWh = 847.59
//   Network  0.1482/kWh = 1,422.57   Retail   RM20 flat   = 20.00
//   KWTBB 1.6% of (energy+capacity+network) = 77.84
//   Caj Semasa (excl AFA) = 4,962.61   (bill's 5,035.88 includes AFA + rounding)
// AFA (Automatic Fuel Adjustment) is included ONLY when the AFA toggle is ON
// (getAfaOn(), shared with the RSC calc). Default OFF (user 2026-07-02): the entered
// Monthly Bill is the ex-AFA figure, so the Original breakdown reconciles to it.
// No Service Tax (ST) applies at this LV-commercial tier.
const LV_ENERGY_RATE   = 0.2703;
const LV_CAPACITY_RATE = 0.0883;
const LV_NETWORK_RATE  = 0.1482;
const LV_RETAIL_FLAT   = 20;
const LV_KWTBB_RATE    = 0.016;

// Returns the itemised LV-commercial bill for a monthly kWh. Mirrors the RSC
// calcC2Bill shape so the Combined card can render the 原电费/新电费 columns.
export function calcCommercialLvBill(kwh, tariffRate) {
    const k = Math.max(0, Number(kwh) || 0);
    let energy = k * LV_ENERGY_RATE;
    let capacity = k * LV_CAPACITY_RATE;
    let network = k * LV_NETWORK_RATE;
    let retail = k > 0 ? LV_RETAIL_FLAT : 0;
    let kwtbb = (energy + capacity + network) * LV_KWTBB_RATE;
    // The itemisation above is the LV "Bukan Domestik Am Voltan Rendah" tariff and is
    // exact ONLY for LV (~0.515/kWh). For MV/HV/Agriculture the agent picks a different
    // single rate, so scale the whole (ex-AFA) breakdown to kWh × tariffRate — this keeps
    // the Original bill reconciled to what the agent entered and stops the saving from
    // exceeding the bill. LV (~0.515) → scale ≈ 1. No tariff passed → legacy LV behaviour.
    const t = Number(tariffRate) || 0;
    if (t > 0 && k > 0) {
        const lvExAfa = energy + capacity + network + retail + kwtbb;
        const f = lvExAfa > 0 ? (k * t) / lvExAfa : 1;
        energy *= f; capacity *= f; network *= f; retail *= f; kwtbb *= f;
    }
    // AFA (Automatic Fuel Adjustment) — gated by the on/off toggle (shared with RSC via
    // getAfaOn()). Added ON TOP of the scaled ex-AFA subtotal (the entered bill is ex-AFA).
    const afa = getAfaOn() ? k * getAfaRate() / 100 : 0;
    const total = energy + capacity + network + retail + afa + kwtbb;
    return { kwh: k, energy, capacity, network, retail, afa, kwtbb, total };
}

/**
 * What one kWh of a FLAT-tariff bill is worth: the tariff, plus AFA when AFA is on.
 *
 * THE EXACT INVERSE OF calcCommercialLvBill, and that is not an approximation. The
 * function above scales its whole ex-AFA itemisation so the subtotal lands on
 * `kWh x tariffRate` precisely, then adds `kWh x afaRate/100` on top. So:
 *
 *     total = kWh x (tariffRate + afaRate/100)     =>     kWh = total / that rate
 *
 * No iteration, no solver - one division recovers the consumption behind a bill.
 *
 * WHY THIS EXISTS. mvSolarRate() and lvTouSolarRate() below both already end
 * `+ (getAfaOn() ? getAfaRate()/100 : 0)`. The flat path alone divided by the bare
 * tariff, so an agent entering a real RM143,000 LV bill got 277,670 kWh - the
 * consumption behind RM153,191 - and the Original box then printed that RM153,191 back
 * at them. Oversized the system by 7% and overstated the saving to match.
 * (user 2026-10-26: "i click with afa and no afa. the kwh is the same with the bill" /
 *  "u see the bill is 153k")
 *
 * Commercial AFA has NO exempt threshold - the residential AFA_EXEMPT_KWH (800) does not
 * apply here, and calcCommercialLvBill charges it from the first kWh. Adding a threshold
 * to one side of an inverse pair is how the two stop agreeing.
 */
export function lvFlatSolarRate(tariffRate) {
    const t = Number(tariffRate) || 0;
    if (!(t > 0)) return 0;
    return t + (getAfaOn() ? getAfaRate() / 100 : 0);
}
// ── TNB commercial MV tariff: "Bukan Domestik Am Voltan Sederhana" (ToU) ─────────────
// NOT a per-kWh tariff — most of an MV bill is MAXIMUM DEMAND. Verified line-for-line
// against a real bill (user 2026-09-23): 86,258 kWh peak + 118,565 kWh off-peak, MD 1,236 kW →
//   Energy peak 0.3132/kWh = 27,016.01   Energy off-peak 0.2723/kWh = 32,285.25
//   Capacity RM30.19/kW × MD = 37,314.84 Network RM66.87/kW × MD      = 82,651.32
//   Retail RM200 flat                    KWTBB 1.6% of (energy+capacity+network) = 2,868.28
//   AFA 0.0359/kWh = 7,353.15 (only when the AFA toggle is ON)       Total = 189,688.85
// MD = the PEAK kW read (kW P). Dividing that bill by a flat 0.303 read 626,036 kWh — three
// times the real 204,823 — which oversized every MV proposal and overstated its saving.
// Peak = 14:00–22:00 on weekdays; weekends are off-peak all day (user 2026-09-23).
export const MV_TARIFF_KEY = 0.303;              // the "Medium Voltage (MV)" option's value in TARIFFS
const MV_ENERGY_PEAK   = 0.3132;
const MV_ENERGY_OFF    = 0.2723;
const MV_CAPACITY_KW   = 30.19;
const MV_NETWORK_KW    = 66.87;
const MV_RETAIL_FLAT   = 200;
const MV_KWTBB_RATE    = 0.016;
const MV_PEAK_START_HR = 14;

export const isMvTariff = (rate) => Math.abs((Number(rate) || 0) - MV_TARIFF_KEY) < 1e-9;

// Share of solar output that lands in the peak window: the 14:00–20:00 part of the daily
// generation shape (07:00–20:00, 15-min steps), on the 5 weekdays out of 7. ≈ 0.283.
export const MV_SOLAR_PEAK_FRAC = (() => {
    const from = (MV_PEAK_START_HR - SOLAR_START_HR) * 4;
    const all = SOLAR_SHAPE.reduce((s, x) => s + x, 0);
    const peak = SOLAR_SHAPE.slice(from).reduce((s, x) => s + x, 0);
    return all > 0 ? (peak / all) * (5 / 7) : 0;
})();

// RM saved per kWh of solar the site uses itself: the energy line it displaces (peak or
// off-peak, weighted by when solar produces) plus the 1.6% KWTBB charged on it, plus AFA when
// ON. Capacity and network stay — solar can't promise a lower MD (a cloudy peak still sets it).
export function mvSolarRate() {
    const energy = MV_SOLAR_PEAK_FRAC * MV_ENERGY_PEAK + (1 - MV_SOLAR_PEAK_FRAC) * MV_ENERGY_OFF;
    return energy * (1 + MV_KWTBB_RATE) + (getAfaOn() ? getAfaRate() / 100 : 0);
}

// Itemised MV bill. `mv` = what the bill prints: { peakKwh, offKwh, mdKw }. `kwh` (optional)
// is the kWh still imported after solar; the kWh solar removed comes off the peak and
// off-peak lines in the solar split. Same shape as calcCommercialLvBill so the cards render it.
export function calcCommercialMvBill(mv, kwh, peakFrac = MV_SOLAR_PEAK_FRAC) {
    const peak0 = Math.max(0, Number(mv?.peakKwh) || 0);
    const off0 = Math.max(0, Number(mv?.offKwh) || 0);
    const md = Math.max(0, Number(mv?.mdKw) || 0);
    const total0 = peak0 + off0;
    let peak = peak0, off = off0;
    if (kwh != null && Number(kwh) < total0) {
        const cut = total0 - Math.max(0, Number(kwh) || 0);
        // How the removed kWh splits across the two energy lines. The caller passes the
        // share worked out from ITS operating hours and days; the module constant is only
        // the fallback for callers that have no schedule to offer.
        const peakCut = Math.min(peak0, cut * peakFrac);
        peak = peak0 - peakCut;
        off = Math.max(0, off0 - (cut - peakCut));
    }
    const k = peak + off;
    const energyPeak = peak * MV_ENERGY_PEAK;
    const energyOff = off * MV_ENERGY_OFF;
    const energy = energyPeak + energyOff;
    const capacity = md * MV_CAPACITY_KW;
    const network = md * MV_NETWORK_KW;
    const retail = (total0 > 0 || md > 0) ? MV_RETAIL_FLAT : 0;
    const kwtbb = (energy + capacity + network) * MV_KWTBB_RATE;
    const afa = getAfaOn() ? k * getAfaRate() / 100 : 0;
    const total = energy + capacity + network + retail + afa + kwtbb;
    return { kwh: k, peakKwh: peak, offKwh: off, mdKw: md, energyPeak, energyOff, energy, capacity, network, retail, afa, kwtbb, total };
}

/**
 * A BILL BACK INTO kWh, on MV — by inverting calcCommercialMvBill, never by dividing.
 *
 * Dividing an MV bill by a sen/kWh rate is the single worst thing you can do on this
 * tariff. Roughly HALF an MV bill is capacity + network, charged per kW of demand and
 * completely unmoved by how many units are consumed. On the ALL COSMOS bill the real
 * average is 55.82 sen/kWh while the solar value is 32.51 sen; divide by the latter and
 * you invent 72% more electricity than the site uses, which then flows into the sizing,
 * the bill comparison and the saving. (user 2026-10-30)
 *
 * The bill is linear in kWh once the demand part is removed:
 *   bill = kWh·e·(1+kwtbb) + kWh·afa + md·(cap+net)·(1+kwtbb) + retail
 * so kWh falls straight out. `e` is the peak/off blend of the month being inverted,
 * taken from the split the user entered — a month is not re-split, only re-scaled.
 *
 * Returns 0 when the demand charges alone already exceed the bill, which means the
 * inputs disagree rather than that the site used no electricity.
 */
export function mvKwhFromBill(mv, rm) {
    const bill = Number(rm) || 0;
    const peak0 = Math.max(0, Number(mv?.peakKwh) || 0);
    const off0 = Math.max(0, Number(mv?.offKwh) || 0);
    const md = Math.max(0, Number(mv?.mdKw) || 0);
    const total0 = peak0 + off0;
    // No split on file → assume the tariff's own shape rather than guess a flat rate.
    const peakFrac = total0 > 0 ? peak0 / total0 : 0.25;
    const energyRate = peakFrac * MV_ENERGY_PEAK + (1 - peakFrac) * MV_ENERGY_OFF;
    const afa = getAfaOn() ? getAfaRate() / 100 : 0;
    const perKwh = energyRate * (1 + MV_KWTBB_RATE) + afa;
    if (!(perKwh > 0)) return 0;
    const fixed = md * (MV_CAPACITY_KW + MV_NETWORK_KW) * (1 + MV_KWTBB_RATE)
        + ((total0 > 0 || md > 0) ? MV_RETAIL_FLAT : 0);
    return Math.max(0, (bill - fixed) / perKwh);
}

// ── TNB commercial LV ToU tariff: "Bukan Domestik Am Voltan Rendah — ToU" ───────
// A DIFFERENT tariff from the flat LV above, not a variant of it: energy is split
// peak/off-peak, and the bill carries a rebate line the flat tariff has none of.
// Verified line-for-line against a real bill (user 2026-10-23), 289,581 kWh total:
//   Peak      60,106 × 0.2852 = 17,142.23    Off-peak 229,475 × 0.2443 = 56,060.74
//   AFA      289,581 × 0.038  = 11,004.08    Capacity 289,581 × 0.0883 = 25,570.00
//   Network  289,581 × 0.1482 = 42,915.90    Retail   RM20 flat
//   Rebate   289,581 × -0.08  = -23,166.48
//   Caj Penggunaan Bulan Semasa = 129,546.47   KWTBB = 1,896.36   Caj Semasa = 131,442.83
//
// TWO THINGS THAT ARE NOT OBVIOUS, and both are why this needed its own function:
//
// 1. CAPACITY IS PER kWh HERE (0.0883/kWh), not per kW as on MV. The bill prints a
//    Maximum Demand (562 kW on the verified bill) and then charges NOTHING for it. Reusing
//    the MV shape would have invented a five-figure demand charge that does not exist.
//
// 2. KWTBB EXCLUDES AFA AND RETAIL. 1.6% of the printed total gives 2,072.74; the bill says
//    1,896.36. It is 1.6% × (energy + capacity + network + rebate) — the rebate counted IN,
//    AFA and the RM20 retail charge counted OUT. Same shape as the flat-LV and MV formulas
//    above, which also levy it on energy+capacity+network only.
export const LV_TOU_TARIFF_KEY = 0.4539;        // its value in the TARIFFS dropdown
const LV_TOU_ENERGY_PEAK = 0.2852;
const LV_TOU_ENERGY_OFF  = 0.2443;
const LV_TOU_CAPACITY    = 0.0883;              // per kWh, NOT per kW
const LV_TOU_NETWORK     = 0.1482;              // per kWh
const LV_TOU_RETAIL_FLAT = 20;
const LV_TOU_REBATE      = -0.08;               // per kWh, negative by nature
const LV_TOU_KWTBB_RATE  = 0.016;

export const isLvTouTariff = (rate) => Math.abs((Number(rate) || 0) - LV_TOU_TARIFF_KEY) < 1e-9;

// Share of solar output landing in the peak window. Peak is 14:00-22:00 on weekdays, the
// same window MV uses, so the same derivation from the generation shape applies.
export const LV_TOU_SOLAR_PEAK_FRAC = MV_SOLAR_PEAK_FRAC;

// RM saved per kWh of solar the site consumes itself: the energy line it displaces,
// weighted by when solar actually produces, plus capacity and network — BOTH of which are
// per-kWh here and so genuinely fall away with every kWh not imported, unlike MV where they
// follow maximum demand and solar cannot promise to lower it. The rebate is per-kWh too, so
// importing less also loses some rebate: it is negative, and nets off the saving.
export function lvTouSolarRate() {
    const energy = LV_TOU_SOLAR_PEAK_FRAC * LV_TOU_ENERGY_PEAK
                 + (1 - LV_TOU_SOLAR_PEAK_FRAC) * LV_TOU_ENERGY_OFF;
    const perKwh = energy + LV_TOU_CAPACITY + LV_TOU_NETWORK + LV_TOU_REBATE;
    return perKwh * (1 + LV_TOU_KWTBB_RATE) + (getAfaOn() ? getAfaRate() / 100 : 0);
}

// Itemised LV ToU bill. `lv` = what the bill prints: { peakKwh, offKwh }. `kwh` (optional)
// is the kWh still imported after solar; what solar removed comes off the peak and off-peak
// lines in the same proportion solar produces. Same shape as the LV and MV calculators so
// the Combined card renders it unchanged.
export function calcCommercialLvTouBill(lv, kwh) {
    const peak0 = Math.max(0, Number(lv?.peakKwh) || 0);
    const off0 = Math.max(0, Number(lv?.offKwh) || 0);
    const total0 = peak0 + off0;

    let peak = peak0, off = off0;
    if (kwh != null && Number(kwh) < total0) {
        const cut = total0 - Math.max(0, Number(kwh) || 0);
        const peakCut = Math.min(peak0, cut * LV_TOU_SOLAR_PEAK_FRAC);
        peak = peak0 - peakCut;
        off = Math.max(0, off0 - (cut - peakCut));
    }

    const k = peak + off;
    const energyPeak = peak * LV_TOU_ENERGY_PEAK;
    const energyOff = off * LV_TOU_ENERGY_OFF;
    const energy = energyPeak + energyOff;
    const capacity = k * LV_TOU_CAPACITY;
    const network = k * LV_TOU_NETWORK;
    const retail = total0 > 0 ? LV_TOU_RETAIL_FLAT : 0;
    const rebate = k * LV_TOU_REBATE;                       // negative
    const afa = getAfaOn() ? k * getAfaRate() / 100 : 0;
    // AFA and retail are deliberately OUT of the base - see note 2 above.
    const kwtbb = (energy + capacity + network + rebate) * LV_TOU_KWTBB_RATE;
    const total = energy + capacity + network + retail + rebate + afa + kwtbb;

    return {
        kwh: k, peakKwh: peak, offKwh: off,
        energyPeak, energyOff, energy, capacity, network, retail, rebate, afa, kwtbb, total,
    };
}

// Effective average RM/kWh under the LV tariff (used to drive saving math so the
// card breakdown and the saving figure share ONE source of truth).
export function commercialLvRatePerKwh(kwh, tariffRate) {
    const k = Math.max(0, Number(kwh) || 0);
    if (k <= 0) return LV_ENERGY_RATE + LV_CAPACITY_RATE + LV_NETWORK_RATE;
    return calcCommercialLvBill(k, tariffRate).total / k;
}

// Commercial pricing tiers (2026-06 list). Solis REMOVED. Brands: goodwe | huawei.
// type: 'string' | 'hybrid'. Battery systems must be Hybrid (GoodWe only).
// - goodwe: String (all tiers) + Hybrid (<100 only — ≥100 falls back to String price).
//   <26 kWp uses the residential row as-is.
// Huawei and Solis have no table of their own: Huawei = GoodWe String + RM100/kWp, Solis =
// GoodWe String flat, both handled in getCommercialPrice. A second Huawei table lived here
// and had already drifted to the wrong rate, so it is gone. (2026-09-26)
const COMMERCIAL_PRICING = {
    goodwe: {
        residentialDiscountPerKwp: 0,
        string: [
            { maxKwp: 30,    pricePerKwp: 1925 },
            { maxKwp: 99.99, pricePerKwp: 1750 },
            { maxKwp: 400,   pricePerKwp: 1700 },
            { maxKwp: Infinity, pricePerKwp: 1600 },
        ],
        hybrid: [
            { maxKwp: 30,    pricePerKwp: 1975 },
            { maxKwp: 99.99, pricePerKwp: 1800 },
            { maxKwp: 400,   pricePerKwp: 1700 },   // no hybrid tier ≥100 → String price
            { maxKwp: Infinity, pricePerKwp: 1600 },
        ],
    },
};

// Auto-Huawei rule.
const HUAWEI_THRESHOLD_KWP = 100;
// Huawei pricing (user 2026-08-04): selectable at ANY kWp.
//   >=26 kWp → GoodWe STRING rate + RM100/kWp.
// <26 kWp brand STRING adjustments off the residential price (added as an under-inverter
// row, NOT an add-on-column row). GoodWe <26 = hybrid (resi as-is, String greyed out).
// Solis −RM2000, Huawei −RM500. (user 2026-08-04; placeholders, refine later)
export const HUAWEI_PREMIUM_PER_KWP = 100;
export const BRAND_STRING_ADJ_SMALL = { solis: -2000, huawei: -500 };

// Systems with inverter capacity above 72 kW require a Suruhanjaya Tenaga
// (ST) license. Approximate fee RM 700–900/month — using a midpoint of 800.
// Applies to BOTH ATAP and SELCo whenever the candidate's inverter exceeds
// the threshold. ATAP is NOT hard-capped at 72 kW — the inverter still
// follows Max Demand × ratio cap. The threshold is purely a billing/licensing
// switch that deducts the monthly fee from savings (and surfaces ROI
// inclusive + exclusive of the fee on the card).
const ST_LICENSE_THRESHOLD_KW = 72;
const ST_LICENSE_MONTHLY_FEE = 800;

// ST-license-aware inverter ceiling (per user): never propose an inverter in the
// 72–100 kW "dead zone" — it triggers the ST license fee for little extra value.
// Cap such systems at 72 kW (the license-free maximum). Only genuine ≥100 kW
// plants go above. buildCandidate then drops any kWp that would exceed the ratio
// cap on the capped inverter, so the 72→100 kW range is skipped entirely.
// Break-free at 90 kW (user 2026-06-24): a marginal overshoot into 73–89 kW isn't
// worth the bigger system, so cap those to 72. But ≥ 90 kW means the bill genuinely
// needs it → propose the REAL inverter (90/95/…), so big commercial bills aren't
// throttled down to ~96 kWp. No ST license fee is charged on these (per user).
const ST_BREAKFREE_KW = 90;
function capStInverter(kw) {
    return (kw > ST_LICENSE_THRESHOLD_KW && kw < ST_BREAKFREE_KW) ? ST_LICENSE_THRESHOLD_KW : kw;
}

// ── Available inverter MODELS (kW) — sizing steps up/down through these only ──
// (user 2026-07-22). String uses the fixed models up to 80 kW, then +10 kW steps to
// 3000 kW, plus the special 115 kW model (real stock).
// GoodWe only BUILDS a hybrid up to 20 kW; bigger hybrids are several of those PCS
// wired together, and we quote the TOTAL kW only, never the unit count (user 2026-09-26).
// Three units is where it stops — 60 kW. Above that the system is String, and a battery
// on it is AC-coupled.
const HYBRID_MAX_KW = 60;
const HYBRID_MODELS = [5, 8, 10, 12, 15, 20, 30, 40, 50, 60];
const STRING_MODELS = [5, 8, 10, 12, 15, 20, 30, 36, 40, 50, 60, 72, 80];
const STRING_MAX_KW = 3000;
// Huawei sells its own sizes and only these (user 2026-09-26). Quoting a 72 kW Huawei was
// quoting a box that does not exist — the GoodWe/Solis ladder was being used for every brand.
// Above 150 kW, units are combined in 150s (300, 450 …) the way the big sites are built.
export const HUAWEI_MODELS = [10, 20, 30, 36, 40, 50, 100, 150];
const huaweiAtLeast = (t) => HUAWEI_MODELS.find(m => m >= t) ?? Math.min(STRING_MAX_KW, Math.ceil(t / 150) * 150);
const huaweiAtMost = (t) => {
    let best = 0;
    for (const m of HUAWEI_MODELS) if (m <= t && m > best) best = m;
    if (t > 150) best = Math.min(STRING_MAX_KW, Math.floor(t / 150) * 150);
    return best || HUAWEI_MODELS[0];
};
// Smallest available model >= target kW (step UP to the next real model).
// Hybrid tops out at 60 kW — anything larger auto-uses the String ladder
// (a >60 kW system can't be hybrid, so it's String for all, battery AC-coupled).
export function inverterModelAtLeast(targetKw, type = 'string', brand = '') {
    const t = Math.max(0, Number(targetKw) || 0);
    if (String(brand).toLowerCase() === 'huawei') return huaweiAtLeast(t);
    if (type === 'hybrid' && t <= HYBRID_MAX_KW) return HYBRID_MODELS.find(m => m >= t) ?? HYBRID_MAX_KW;
    // String (or hybrid target > 40 kW → String):
    const base = STRING_MODELS.find(m => m >= t);
    if (base != null) return base;                                        // <= 80 kW
    if (t > 110 && t <= 115) return 115;                                  // special 115 (110<..<=115)
    return Math.min(STRING_MAX_KW, Math.max(90, Math.ceil(t / 10) * 10)); // 90,100,110,... to 3000
}
// Largest available model <= target kW (MD-based sizing — don't exceed demand).
// Hybrid > 60 kW falls to the String ladder (same rule).
export function inverterModelAtMost(targetKw, type = 'string', brand = '') {
    const t = Math.max(0, Number(targetKw) || 0);
    if (String(brand).toLowerCase() === 'huawei') return huaweiAtMost(t);
    const useHybrid = type === 'hybrid' && t <= HYBRID_MAX_KW;
    const list = useHybrid ? HYBRID_MODELS : STRING_MODELS;
    let best = 0;
    for (const m of list) if (m <= t && m > best) best = m;
    if (!useHybrid && t > 80) {
        best = Math.min(STRING_MAX_KW, Math.floor(t / 10) * 10);          // 90,100,... down
        if (t >= 115 && best < 115) best = 115;                           // 115 special
    }
    return Math.max(5, best || 5);
}
// True once a system needs an inverter larger than the biggest hybrid stack (60 kW):
// it must be String for all, and any battery on it is AC-coupled.
export const exceedsHybridMax = (kwp, ratioCap = 1.45) => ((Number(kwp) || 0) / ratioCap) > HYBRID_MAX_KW;

export const BRAND_LABEL = { solis: 'Solis', goodwe: 'GoodWe', huawei: 'Huawei' };
export const BRAND_TYPE  = { solis: 'String', goodwe: 'Hybrid', huawei: 'String' };

// ── Commercial <26 kWp inverter auto-upgrade ─────────────────────────────────
// Parse the inverter kW from the ATAP row's InverterSize. The rows use the "{phase}p{kw}k"
// format ("1p5k" = 5 kW single-phase, "3p8k" = 8 kW 3-phase) — take the number BETWEEN p
// and k, NOT a naive digit-strip (which turned "1p5k" into "15" → a bogus 15 kW inverter and
// ratio 0.48). Falls back to plain "8kW"/"8" for any non-Xp Yk value. (fix user 2026-08-04)
const parseInvKw = (s) => {
    const str = String(s || '');
    const m = /p\s*(\d+(?:\.\d+)?)\s*k/i.exec(str);
    if (m) return parseFloat(m[1]) || 0;
    return parseFloat(str.replace(/[^\d.]/g, '')) || 0;
};
// ATAP-included 3-phase inverter by panel count — kept ONLY as a fallback for when the price
// row / InverterSize column hasn't loaded, so the upgrade never silently no-ops. (3-phase only)
const atapInverter3pKw = (panels) => {
    const p = Number(panels) || 0;
    if (p <= 0) return 0;
    if (p <= 13) return 5;
    if (p <= 21) return 8;
    if (p <= 26) return 10;
    if (p <= 32) return 12;
    return 15;
};
// Residential band: the ATAP-included inverter (sized to the looser residential ratio) is often too
// small for the commercial 1.42 cap, so bump it to the smallest SKU that clears the ratio and
// fold in the LIVE "Inverter Upgrade XpA to XpB" add-on prices (resourceaddon). Now PHASE-AWARE
// and DATA-DRIVEN via the shared RSC walker (suggestInverterUpgrade): 1-phase walks the 1p ladder
// and CAPS at 1p8 (same-phase only → never crosses to 3-phase); 3-phase walks the 3p ladder.
// Base inverter is read from the matched ATAP row (same source RSC uses). Returns { kw, cost,
// steps=addonIds }. (user 2026-08-03 — replaces the old hardcoded 3p-only cost table)
export function commercialInverterUpgrade(kwp, panels, { phaseMode = '3phase', addonCatalog = [], rows = [] } = {}) {
    const k = Number(kwp) || 0, p = Number(panels) || 0;
    const is3p = phaseMode === '3phase';
    const row = (rows || []).find(r => Number(r.panels) === p);
    // Base ATAP-included inverter from the matched row (phase-appropriate); fall back to the
    // 3p panel formula only if the row / InverterSize column hasn't loaded.
    let base = parseInvKw(is3p ? (row?.inverter3p || row?.inverter) : (row?.inverter || ''));
    if (base <= 0 && is3p) base = atapInverter3pKw(p);
    if (k <= 0 || base <= 0) return { kw: base, cost: 0, steps: [] };
    const up = suggestInverterUpgrade({ kwp: k, naturalKw: base, phaseMode, addonCatalog });
    if (!up) return { kw: base, cost: 0, steps: [] };   // already big enough, or 1p can't clear past 1p8
    return { kw: up.targetKw, cost: up.cost, steps: up.addonIds };
}

/**
 * Pick a commercial inverter size from Max Demand by rounding DOWN to the
 * nearest 5 kW step (real-world commercial inverter SKUs are 5/10/15/20/25/30...).
 * Examples: MD 24 → 20 kW, MD 26 → 25 kW, MD 30 → 30 kW, MD 75 → 75 kW.
 */
export function inverterFromMd(maxDemandKw, type = 'string', brand = '') {
    // Largest available inverter model that doesn't exceed the max demand.
    return inverterModelAtMost(Number(maxDemandKw) || 0, type, brand);
}

/**
 * Compute total system price.
 *
 * For kWp <= 30 with solis/goodwe: starts from the matched Residential Pricing
 * row's cash and subtracts the brand's residentialDiscountPerKwp × kwp.
 * For kWp > 30 (or any kwp with Huawei): uses tier rate × kwp.
 */
export function getCommercialPrice({ kwp, brand, type = 'string', residentialCash, is3p }) {
    // <=30 kWp (ANY brand incl. Solis/Huawei): raw residential ATAP price. Brand STRING
    // discounts (Solis −RM2000, Huawei −RM500) are added SEPARATELY as an under-inverter
    // row, so systemCost stays the raw residential base. (user 2026-08-04)
    if (kwp <= 30) {
        if (!residentialCash) return 0;
        const disc = COMMERCIAL_PRICING[brand]?.residentialDiscountPerKwp || 0;
        return Math.round(residentialCash - disc * kwp);
    }
    // Huawei >30 (String-only): GoodWe STRING rate + RM100/kWp. (user 2026-08-04)
    if (brand === 'huawei') {
        const stiers = COMMERCIAL_PRICING.goodwe.string;
        const stier = stiers.find(t => kwp <= t.maxKwp) || stiers[stiers.length - 1];
        return Math.round(kwp * (stier.pricePerKwp + HUAWEI_PREMIUM_PER_KWP));
    }
    // Solis >30 (String-only): plain GoodWe STRING rate (no premium). It has no own key in
    // COMMERCIAL_PRICING, so route it through GoodWe String (matches commercialPricePerKwp).
    if (brand === 'solis') {
        const stiers = COMMERCIAL_PRICING.goodwe.string;
        const stier = stiers.find(t => kwp <= t.maxKwp) || stiers[stiers.length - 1];
        return Math.round(kwp * stier.pricePerKwp);
    }
    const conf = COMMERCIAL_PRICING[brand];
    if (!conf) return 0;
    const tiers = conf[type] || conf.string;
    const tier = tiers.find(t => kwp <= t.maxKwp) || tiers[tiers.length - 1];
    return Math.round(kwp * tier.pricePerKwp);
}

/**
 * PER-SLOT workday energy flow (ATAP) — the SAME instantaneous model as SELCo, but the
 * surplus EXPORTS (at SMP) instead of curtailing. The daytime consumption sits in the
 * solar hours (operating ∩ 08:00–18:00); self = Σ min(solar_slot, load_slot); the midday
 * peak that exceeds the instantaneous load exports. Keeps ATAP and SELCo consistent on
 * the same system. (user 2026-07-25)
 *
 * Returns { self, exported, dailyGen, overlapHours, selfFrac }.
 */
function workdayFlow({ kwp, dailyConsumption, opStart, opEnd }) {
    const dayStart = Math.max(opStart, 8), dayEnd = Math.min(opEnd, 18);
    const dayHours = Math.max(0, dayEnd - dayStart);
    const loadKW = dayHours > 0 ? dailyConsumption / dayHours : 0;
    const STEP = 0.25;   // 15-minute resolution
    let dailyGen = 0, self = 0, exported = 0, selfPeak = 0, selfOff = 0;
    for (let t = SOLAR_START_HR; t < SOLAR_END_HR; t += STEP) {
        const mid = t + STEP / 2;
        const solarE = solarKwAt(kwp, mid) * STEP;             // kWh this slot
        dailyGen += solarE;
        const inDay = mid >= dayStart && mid < dayEnd;
        const loadE = inDay ? loadKW * STEP : 0;
        const used = Math.min(solarE, loadE);
        self += used;
        exported += solarE - used;                             // ATAP: surplus exports at SMP
        /* WHICH HALF OF THE BILL this kWh came off. The ToU peak window runs 14:00–22:00
           and solar stops at 20:00, so every slot from 14:00 onwards displaces PEAK energy
           and everything before it displaces OFF-PEAK — at 0.3132 against 0.2723, a 15%
           difference in what the kWh is worth.

           It is recorded HERE, inside the loop that already decides self-use against the
           operating window, because the split belongs to the kWh actually DISPLACED. The
           old module constant measured raw GENERATION instead, so a site closing at 17:00
           was credited for its 17:00–20:00 output — the most peak-weighted hours of the
           day, and precisely the ones it exports. (user 2026-10-31) */
        if (mid >= MV_PEAK_START_HR) selfPeak += used; else selfOff += used;
    }
    return {
        self,
        exported,
        dailyGen,
        overlapHours: dayHours,
        selfFrac: dailyGen > 0 ? self / dailyGen : 0,
        selfPeak,
        selfOff,
    };
}

/**
 * Monthly aggregate using the bell-curve workday split. NEM mode — export
 * to grid pays SMP. Off-day generation also exports at SMP.
 */
export function estimateMonthlySaving({ kwp, tariffRate, monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal = 0, offHourBaselinePct = 0.2 }) {
    const workdaysPerMonth = operatingDays * WORKWEEKS_PER_MONTH;
    const offdaysPerMonth = (7 - operatingDays) * WORKWEEKS_PER_MONTH;
    // Split monthly consumption into op-hour vs off-hour. Solar can only cover
    // op-hour consumption directly — the off-hour baseline (overnight + weekend
    // standby) is grid-imported.
    const baseline = Math.min(0.5, Math.max(0, offHourBaselinePct));
    const monthlyOpConsumption = monthlyKwh * (1 - baseline);
    const dailyConsumption = workdaysPerMonth > 0 ? monthlyOpConsumption / workdaysPerMonth : 0;

    const work = workdayFlow({ kwp, dailyConsumption, opStart, opEnd });
    const battStorageDaily = battKwhTotal * 0.9 * 0.9;
    // The battery charges from the midday surplus and discharges to cover load the DIRECT
    // solar missed — the operating-hour deficit (morning/evening ramp) PLUS the off-hour
    // (night) baseline. It can NEVER absorb more than it can later discharge, so it never
    // wastes energy or reduces the saving; with nothing to discharge into, the surplus just
    // exports as before (battery = no effect, not a drop). (user 2026-07-25)
    const offHourConsumption = monthlyKwh * baseline;
    const dailyOpDeficit = Math.max(0, dailyConsumption - work.self);
    const dailyNightLoad = workdaysPerMonth > 0 ? offHourConsumption / workdaysPerMonth : 0;
    const battAbsorbed = Math.min(work.exported, battStorageDaily, dailyOpDeficit + dailyNightLoad);
    const workdayExport = work.exported - battAbsorbed;

    // Generation spans the SAME real day count as consumption/self (workdays + offdays
    // = 7 × 4.345 = 30.415), NOT a flat 30. Using 30 here while self summed over 30.415
    // workdays made self-use exceed 100% of generation. (user flagged 2026-07-24)
    const monthGenerated = work.dailyGen * (workdaysPerMonth + offdaysPerMonth);
    // battAbsorbed is already capped by what the battery can discharge (op-deficit + night
    // load), so every stored kWh becomes self-use — no waste, no double-count. (2026-07-25)
    const monthBattSelf = workdaysPerMonth * battAbsorbed;
    const monthSelf = workdaysPerMonth * work.self + monthBattSelf;
    const monthExportedRaw = workdaysPerMonth * workdayExport + offdaysPerMonth * work.dailyGen;

    // NEM/ATAP rule: export credit caps at the kWh the customer still needs
    // to draw from grid after self-consumption (= monthlyKwh − monthSelf).
    // Anything beyond that is generated but can't be paid for in this month.
    const monthImportAfterSolar = Math.max(0, monthlyKwh - monthSelf);
    const monthExported = Math.min(monthExportedRaw, monthImportAfterSolar);
    const monthExportLost = monthExportedRaw - monthExported;

    /* THE MONTH'S PEAK SHARE, from the clock and the calendar rather than a constant.
       Peak energy only exists on WEEKDAYS, so operating days beyond five are weekend days
       where every displaced kWh is off-peak however late in the afternoon it was made. A
       7-day site therefore gets a LOWER peak share than its daily curve implies.

       Battery self-use rides the same fraction as the direct self-use: the model has no
       discharge schedule, and inventing one would be a fiction dressed as precision. */
    const weekdayOps = Math.min(operatingDays, 5);
    const weekendOps = Math.max(0, operatingDays - 5);
    const selfPeakDirect = weekdayOps * WORKWEEKS_PER_MONTH * work.selfPeak;
    const selfOffDirect = weekdayOps * WORKWEEKS_PER_MONTH * work.selfOff
        + weekendOps * WORKWEEKS_PER_MONTH * work.self;
    const directSelf = selfPeakDirect + selfOffDirect;
    const selfPeakFrac = directSelf > 0 ? selfPeakDirect / directSelf : 0;

    const originalSaving = monthGenerated * tariffRate;
    const monthlySaving = monthSelf * tariffRate + monthExported * SMP_RATE;
    const exportPct = monthGenerated > 0 ? monthExported / monthGenerated : 0;
    const lostExportPct = monthGenerated > 0 ? monthExportLost / monthGenerated : 0;

    return {
        generated: monthGenerated,
        selfConsumed: monthSelf,
        importAfterSolar: monthImportAfterSolar,
        exported: monthExported,
        exportPct,
        // Which tariff window the self-used kWh came off — drives the ToU bill lines
        // and the chart's peak/off-peak split.
        selfPeakFrac,
        selfPeakKwh: monthSelf * selfPeakFrac,
        selfOffKwh: monthSelf * (1 - selfPeakFrac),
        exportedRaw: monthExportedRaw,
        exportLost: monthExportLost,
        lostExportPct,
        curtailed: 0,
        curtailedPct: 0,
        originalSaving,
        monthlySaving,
        overlapHours: work.overlapHours,
        workdaySelfPct: work.selfFrac,
        // Battery daily fill: how much of the usable storage the day's solar surplus can
        // actually charge. < 100% ⇒ the battery can't fill up (shown as a warning).
        battUsableKwh: battStorageDaily,
        battDailyChargeKwh: battAbsorbed,
        battChargePct: battStorageDaily > 0 ? Math.min(100, Math.round(battAbsorbed / battStorageDaily * 100)) : 100,
        // Raw daily solar surplus (generation − daytime self-use) available to charge a
        // battery — used to size Option B's battery to what one day can fully charge.
        dailySurplusKwh: work.exported,
        // What the battery actually CYCLES per day = charged kWh capped by the night load
        // it can discharge into. If used < charged, the battery is oversized (a bigger
        // pack charges more but can't use it — e.g. 32 kWh saving = 16 kWh saving).
        battDailyUsedKwh: workdaysPerMonth > 0 ? monthBattSelf / workdaysPerMonth : 0,
        // Monthly battery flow for the card: charged (solar → battery) vs used (battery →
        // load). If used ≪ charged the battery is oversized/wasted. (user 2026-07-25)
        battMonthlyChargeKwh: workdaysPerMonth * battAbsorbed,
        battMonthlySelfKwh: monthBattSelf,
    };
}

// Solar POWER (kW) at clock-hour t — the empirical Malaysian shape over 07:00–20:00
// (SOLAR_SHAPE, 15-min). Σ(shape)×dailyGen×0.25h = dailyGen (= kWp × YIELD_PER_KWP), so
// the whole-day integral is the yield. Peaks ~13:00. Outside the window → 0. (user 2026-07-24)
export function solarKwAt(kwp, t) {
    if (t < SOLAR_START_HR || t >= SOLAR_END_HR) return 0;
    const idx = Math.floor((t - SOLAR_START_HR) / 0.25);
    const w = (idx >= 0 && idx < SOLAR_SHAPE.length) ? SOLAR_SHAPE[idx] : 0;
    return w * (kwp * YIELD_PER_KWP);
}

// Solar ENERGY (kWh) generated in the hour starting at clock-hour h — integral of the
// empirical curve over [h, h+1] (four 15-min sub-slots). Σ over 24h = kWp × 3.4. Shared
// source of truth for the hourly explainer + synthetic load-profile chart.
export function solarKwhInHour(kwp, h) {
    let e = 0;
    for (let t = h; t < h + 1 - 1e-9; t += 0.25) e += solarKwAt(kwp, t + 0.125) * 0.25;
    return e;
}
export { YIELD_PER_KWP as CSC_YIELD_PER_KWP, WORKWEEKS_PER_MONTH as CSC_WORKWEEKS_PER_MONTH };

// SELCo (zero-export) workday energy flow with a BELL-CURVE solar profile and an
// INSTANTANEOUS load cap. Without a battery, solar above the load at any moment
// is curtailed (can't export, can't store). A battery stores that per-slot excess
// (90% round-trip) and discharges to cover op-hour load the direct solar missed.
//   The daytime consumption sits in the SOLAR hours (operating ∩ 08:00–18:00), where the
//   sun actually produces — NOT spread flat over the whole operating window. Spreading it
//   flat left the midday peak under-loaded and heavily over-curtailed (understated self-use).
//   This matches the Astern simulator's per-slot model. (user 2026-07-25)
//   loadKW   = daytime load concentrated in the solar hours = dailyOpConsumption / dayHours
//   selfDirect = Σ min(solar_slot, load_slot)         (instantaneous)
//   excess     = Σ (solar_slot − used)                (curtailable / batteryable)
//   battery    = min(excess, B×0.9) discharged into the unmet daytime load
function selcoWorkdayFlow({ kwp, dailyOpConsumption, opStart, opEnd, battKwhTotal = 0 }) {
    const dayStart = Math.max(opStart, 8), dayEnd = Math.min(opEnd, 18);
    const dayHours = Math.max(0, dayEnd - dayStart);
    const loadKW = dayHours > 0 ? dailyOpConsumption / dayHours : 0;
    const STEP = 0.25;   // 15-minute resolution
    let dailyGen = 0, selfDirect = 0, excessSolar = 0;
    for (let t = SOLAR_START_HR; t < SOLAR_END_HR; t += STEP) {
        const mid = t + STEP / 2;
        const solarE = solarKwAt(kwp, mid) * STEP;             // kWh this slot
        dailyGen += solarE;
        const inDay = mid >= dayStart && mid < dayEnd;         // daytime load window (solar hours)
        const loadE = inDay ? loadKW * STEP : 0;
        const used = Math.min(solarE, loadE);
        selfDirect += used;
        excessSolar += solarE - used;
    }
    const battUsable = (Number(battKwhTotal) || 0) * 0.9 * 0.9; // RTE × DOD (= 0.81), matches ATAP CSC + RSC
    const stored = Math.min(excessSolar, battUsable);
    const deficit = Math.max(0, dailyOpConsumption - selfDirect); // daytime load solar didn't meet
    const selfFromBatt = Math.min(stored, deficit);
    return {
        dailyGen,
        self: selfDirect + selfFromBatt,
        selfDirect,
        selfFromBatt,
        stored,
        battUsable,
        excessSolar,
        curtailed: Math.max(0, excessSolar - selfFromBatt),
        loadKW,
    };
}

/**
 * Monthly aggregate for SELCo (Self-Consumption Only) mode — zero export.
 * Uses a bell-curve solar profile + instantaneous load cap (solar above the
 * load at any moment is curtailed without a battery). A battery stores the
 * per-slot excess and discharges into unmet op-hour load. Off-day generation
 * is fully curtailed (no op load; battery not cycled on off-days).
 * Saving = self-consumed kWh × tariff only.
 */
/**
 * Saving worked out from SEVERAL REAL BILLS instead of one typed figure.
 *
 * A proposal built on a single month assumes every month looks like it. They do not:
 * "first month 3k, upcmoming 3k 3k 5k 6k 2k so the saving might be diff with proposal"
 * (user 2026-10-23).
 *
 * WHY THE AVERAGE IS NOT GOOD ENOUGH, and the whole reason this exists. Saving is
 * CAPPED BY CONSUMPTION - a system cannot offset power the site never used. In a heavy
 * month the array is fully absorbed; in a light one the midday surplus is exported at the
 * SMP rate or not used at all. So the saving curve bends over, and the saving on the
 * AVERAGE bill is higher than the AVERAGE of the monthly savings. Sizing on 3k and
 * quoting that figure overstates what the 2k month will actually deliver.
 *
 * Each month is therefore run through the SAME estimateMonthlySaving the single-bill
 * path uses - one engine, so the two methods can never disagree about the same month -
 * and the overstatement is returned explicitly as `optimismPerMonth` rather than left for
 * somebody to notice.
 *
 * `bills` are RM figures, in any order, blanks ignored. `kwhFor` converts one to kWh;
 * it defaults to bill / tariffRate but a ToU tariff passes its own, since dividing a ToU
 * bill by a rate is exactly the mistake that oversized every MV proposal.
 */
export function estimateSavingFromBills({
    bills = [], tariffRate, kwp, operatingDays, opStart, opEnd,
    battKwhTotal = 0, offHourBaselinePct = 0.2, kwhFor = null, systemCost = 0,
}) {
    const toKwh = kwhFor || ((rm) => (Number(tariffRate) > 0 ? rm / Number(tariffRate) : 0));

    /* A month may carry its OWN kWh, and when it does that wins.
       Deriving kWh from RM is a last resort: on a demand-charged tariff the same RM
       means a different number of units the moment MD or AFA moves, so a stored bill
       silently decays. A month read off an interval file knows its kWh exactly — there
       is nothing to infer. (user 2026-10-31: "still 0") */
    const clean = (Array.isArray(bills) ? bills : [])
        .map((b) => (typeof b === 'object' && b !== null
            ? { bill: parseFloat(String(b.bill ?? '').replace(/,/g, '')), kwh: Number(b.kwh) > 0 ? Number(b.kwh) : null }
            : { bill: parseFloat(String(b ?? '').replace(/,/g, '')), kwh: null }))
        .filter((x) => Number.isFinite(x.bill) && x.bill > 0);

    if (clean.length === 0) return null;

    const one = (monthlyKwh) => estimateMonthlySaving({
        kwp, tariffRate, monthlyKwh, operatingDays, opStart, opEnd,
        battKwhTotal, offHourBaselinePct,
    });

    const months = clean.map(({ bill, kwh: givenKwh }) => {
        const kwh = givenKwh != null ? givenKwh : toKwh(bill);
        const r = one(kwh);
        const saving = r?.monthlySaving ?? r?.saving ?? 0;
        return {
            bill,
            kwh,
            // What the array made and how much of it the site actually used, THIS month.
            // The SARE saving turns on this split: a self-used kWh is worth the tariff,
            // an exported one only SMP. (user 2026-10-24)
            generated: r?.generated ?? 0,
            selfConsumed: r?.selfConsumed ?? 0,
            exported: r?.exported ?? 0,
            saving,
            // Share of THIS month's bill the system removes. The number an agent is asked
            // about - "you said 60%, my March bill barely moved" - so it is per month,
            // never an average presented as if it held every month.
            pct: bill > 0 ? (saving / bill) * 100 : 0,
        };
    });

    const sum = (f) => months.reduce((a, m) => a + f(m), 0);
    const avgBill = sum((m) => m.bill) / months.length;
    const avgKwh = sum((m) => m.kwh) / months.length;
    const avgSaving = sum((m) => m.saving) / months.length;

    // What the single-bill method WOULD have quoted, from the average bill. The gap
    // between this and avgSaving is the optimism the averaging hides.
    const flat = one(avgKwh);
    const flatSaving = flat?.monthlySaving ?? flat?.saving ?? 0;

    /* THE YEAR'S SELF-USE MIX, settled month by month and then accumulated - which is
       the right order, and gives the same answer as a blended fraction because the TNB
       tariff is constant within a year:

         per month   Σ(self × tariff) + Σ(export × SMP) − Σ(gen × lease)
         blended     Σgen × [ f×tariff + (1−f)×SMP − lease ],  f = Σself ÷ Σgen

       Null when the engine gave no generation to divide by - the caller must then keep
       its existing assumption rather than silently adopting a zero. */
    const totalGenerated = sum((m) => m.generated);
    const totalSelfUsed = sum((m) => m.selfConsumed);
    const selfUseFraction = totalGenerated > 0
        ? Math.max(0, Math.min(1, totalSelfUsed / totalGenerated))
        : null;

    const savings = months.map((m) => m.saving);

    /* A YEAR IS THE HONEST UNIT. An average month hides both ends - the lean month the
       customer will ring about, and the heavy one that pays for the system - and a
       business with a season has no "typical" month at all.
       (user 2026-10-23: "if six month average the saving aso not correct ... then make
        it yearly saving roi? coos sometimes is a business cycle")

       Twelve months entered = a real year, summed. Fewer = scaled to twelve from what
       was given, and flagged, because six months of a seasonal business can be the busy
       half or the quiet half and nothing here can tell which. */
    const totalSaving = sum((m) => m.saving);
    const annualised = months.length < 12;
    const annualSaving = (totalSaving / months.length) * 12;
    const cost = Number(systemCost) || 0;
    // Simple payback on the annual figure: no escalation, no degradation, no discounting
    // - the same flat basis the rest of the CSC cards use, so they can be compared.
    const paybackYears = cost > 0 && annualSaving > 0 ? cost / annualSaving : null;

    return {
        months,
        count: months.length,
        annualSaving,
        annualised,
        // For the SARE tables: how much of a year's generation this customer actually uses.
        selfUseFraction,
        exportFraction: selfUseFraction == null ? null : 1 - selfUseFraction,
        paybackYears,
        avgBill,
        avgKwh,
        avgSaving,
        minSaving: Math.min(...savings),
        maxSaving: Math.max(...savings),
        totalSaving,
        flatSaving,
        // Positive = the single-bill proposal claims more than these bills support.
        optimismPerMonth: flatSaving - avgSaving,
        leanestMonth: months.reduce((lo, m) => (m.saving < lo.saving ? m : lo), months[0]),
        /* THE HEAVIEST MONTH, by bill - the one the card illustrates. Picked on BILL and
           not on saving: the panel it drives is a bill comparison, so "the big month" has
           to mean the big bill or the heading contradicts the figure beside it. In a month
           that large the site absorbs nearly everything the array makes, which is also the
           honest way to show the system working rather than spilling.
           (user 2026-10-24: "this shd pick the highest month") */
        peakMonth: months.reduce((hi, m) => (m.bill > hi.bill ? m : hi), months[0]),
    };
}

export function estimateMonthlySavingSELCo({ kwp, tariffRate, monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal = 0, offHourBaselinePct = 0.2 }) {
    const workdaysPerMonth = operatingDays * WORKWEEKS_PER_MONTH;
    const offdaysPerMonth = (7 - operatingDays) * WORKWEEKS_PER_MONTH;
    const baseline = Math.min(0.5, Math.max(0, offHourBaselinePct));
    const monthlyOpConsumption = monthlyKwh * (1 - baseline);
    const dailyOpConsumption = workdaysPerMonth > 0 ? monthlyOpConsumption / workdaysPerMonth : 0;

    const flow = selcoWorkdayFlow({ kwp, dailyOpConsumption, opStart, opEnd, battKwhTotal });

    // Generation spans the same real day count as self (workdays + offdays = 30.415),
    // not a flat 30 — otherwise self-use exceeds 100% of gen. (user flagged 2026-07-24)
    const monthGenerated = flow.dailyGen * (workdaysPerMonth + offdaysPerMonth);

    // Op-hour self-use: direct solar + battery covering operating-hour gaps.
    const monthOpSelf = workdaysPerMonth * flow.self;

    // Battery ALSO shifts surplus daytime solar to the OFF-HOUR (night) baseline load —
    // store during the day, discharge at night — exactly like the RSC calcSaving battery
    // (battIn = min(storage, surplus) → battOut covers nightUsage). Cover it from whatever
    // battery capacity + surplus remains after op-hour use. (user 2026-07-04)
    const battUsableMonthly = flow.battUsable * DAYS_PER_MONTH;                        // daily cycle
    const battLeftMonthly = Math.max(0, battUsableMonthly - workdaysPerMonth * flow.selfFromBatt);
    const surplusMonthly = Math.max(0, monthGenerated - monthOpSelf);
    const offHourMonthly = monthlyKwh * baseline;                                      // night load
    const offHourFromBatt = Math.min(offHourMonthly, battLeftMonthly, surplusMonthly);

    const monthSelf = monthOpSelf + offHourFromBatt;
    const monthCurtailed = Math.max(0, monthGenerated - monthSelf);
    const monthImportAfterSolar = Math.max(0, monthlyKwh - monthSelf);

    const originalSaving = monthGenerated * tariffRate;
    const monthlySaving = monthSelf * tariffRate;  // zero export revenue

    // Battery daily throughput for the card (op-hour gaps + night coverage).
    const battDailyUsed = (workdaysPerMonth * flow.selfFromBatt + offHourFromBatt) / DAYS_PER_MONTH;

    return {
        generated: monthGenerated,
        selfConsumed: monthSelf,
        importAfterSolar: monthImportAfterSolar,
        exported: 0,
        exportPct: 0,
        curtailed: monthCurtailed,
        curtailedPct: monthGenerated > 0 ? monthCurtailed / monthGenerated : 0,
        originalSaving,
        monthlySaving,
        overlapHours: Math.max(0, Math.min(opEnd, SOLAR_END_HR) - Math.max(opStart, SOLAR_START_HR)),
        offdaysPerMonth,
        // Battery daily charge/use — so the card can write it in SELCo mode too
        // (mirrors the ATAP estimateMonthlySaving fields).
        battUsableKwh: flow.battUsable,
        battDailyChargeKwh: battDailyUsed,
        battDailyUsedKwh: battDailyUsed,
        battChargePct: flow.battUsable > 0 ? Math.min(100, Math.round(battDailyUsed / flow.battUsable * 100)) : 100,
        dailySurplusKwh: flow.excessSolar,
        // Monthly battery flow (charged vs used) — mirrors ATAP. (user 2026-07-25)
        battMonthlyChargeKwh: workdaysPerMonth * flow.stored,
        battMonthlySelfKwh: workdaysPerMonth * flow.selfFromBatt + offHourFromBatt,
    };
}

/**
 * Right-size an inverter from kWp using the same ratio cap as residential.
 * Rounded UP to a 5 kW SKU step so panels never exceed the ratio.
 */
export function inverterFromKwp(kwp, ratioCap, type = 'string', brand = '') {
    // Smallest available inverter model that keeps kWp within inverter × ratioCap.
    return inverterModelAtLeast((Number(kwp) || 0) / ratioCap, type, brand);
}

/**
 * Math-derived ideal sizing — bill ÷ daily-op-kWh ÷ 3.4 ÷ 0.64, capped by MD.
 */
export function deriveSizing({ monthlyBill, tariffRate, operatingDays, maxDemandKw, battKwhTotal = 0, phaseMode = 'single', mode = 'atap', brand = '' }) {
    const monthlyKwh = tariffRate > 0 ? monthlyBill / tariffRate : 0;
    const workdaysPerMonth = operatingDays * WORKWEEKS_PER_MONTH;
    const dailyKwh = workdaysPerMonth > 0 ? monthlyKwh / workdaysPerMonth : 0;
    const requiredKwp = dailyKwh / YIELD_PER_KWP;
    const requiredPanels = Math.ceil(requiredKwp / PANEL_KWP);

    // Safety ratio fixed at 1.35 (kWp ≤ inverter × 1.35) regardless of battery.
    const ratioCap = RATIO_CAP_NO_BATT;
    const is1p = phaseMode !== '3phase';
    const hasMd = Number(maxDemandKw) > 0;
    const isAtap = mode !== 'selco';

    // Inverter sizing rules:
    // - Single phase: HARD cap at 5 kW (SEDA 1P limit) regardless of mode/MD/bill.
    //   A bigger need just gets capped (UI warns to switch to 3-phase).
    // - 3-phase ATAP WITH Max Demand: inverter from MD (rounded down to 5 kW SKU) —
    //   export validated against demand.
    // - 3-phase ATAP without MD, OR SELCo (any): size from the bill-derived kWp
    //   (no MD ceiling). SELCo never uses MD (zero export).
    let inverterKw;
    // Hybrid inverter models only for a small (LV) battery system that still fits within
    // the 60 kW hybrid ceiling; a bigger system is String for all, battery AC-coupled.
    const invModelType = (battKwhTotal > 0 && battKwhTotal < 100 && !exceedsHybridMax(requiredKwp, ratioCap)) ? 'hybrid' : 'string';
    if (is1p) {
        inverterKw = 5;
    } else if (isAtap && hasMd) {
        inverterKw = inverterFromMd(maxDemandKw, invModelType, brand);
    } else {
        inverterKw = inverterFromKwp(requiredKwp, ratioCap, invModelType, brand);
    }
    const maxKwpByInv = inverterKw * ratioCap;
    const maxPanelsByInv = Math.floor(maxKwpByInv / PANEL_KWP);

    const targetPanels = Math.min(requiredPanels, maxPanelsByInv);
    return {
        monthlyKwh,
        workdaysPerMonth,
        dailyKwh,
        requiredKwp,
        requiredPanels,
        inverterKw,
        maxKwpByInv,
        maxPanelsByInv,
        targetPanels,
        ratioCap,
        is1p,
        hasMd,
        // True when single-phase can't fit the bill within the 5 kW ceiling →
        // UI shows "needs 3-phase" warning.
        single1pCapped: is1p && requiredPanels > maxPanelsByInv,
    };
}

/**
 * Effective brand. 2026-08-04: GoodWe, Solis and Huawei are ALL valid at any kWp
 * (Solis/Huawei String-only) — keep the user's pick; pricing is handled per-brand by
 * getCommercialPrice / commercialPricePerKwp. (was: Solis→GoodWe, Huawei<100→GoodWe)
 */
export function resolveBrand(userBrand, kwp) {
    return (userBrand || 'goodwe').toLowerCase();
}

// Resolve the inverter type. Battery requires Hybrid; Huawei is String-only.
export function resolveType(userType, brand, battKwhTotal = 0) {
    if ((brand || 'goodwe').toLowerCase() === 'huawei') return 'string';
    if (Number(battKwhTotal) > 0) return 'hybrid';
    return (userType || 'string').toLowerCase() === 'hybrid' ? 'hybrid' : 'string';
}

function buildCandidate({ panels, brand, invType = 'string', is3p, residentialRows, monthlyBill, tariffRate, operatingDays, opStart, opEnd, battKwhTotal, breakdown, mode = 'nem', offHourBaselinePct = 0.2, inverterKwOverride = 0, fullSelfUse = false }) {
    const kwp = Math.round(panels * PANEL_KWP * 100) / 100;
    const effectiveBrand = resolveBrand(brand, kwp);
    const effectiveType = resolveType(invType, effectiveBrand, battKwhTotal);

    let residentialCash = 0;
    if (kwp <= 30 && residentialRows?.length) {
        const row = residentialRows.find(r => r.panels === panels);
        if (row) residentialCash = is3p ? row.cash3p : row.cash;
    }

    const cashPrice = getCommercialPrice({ kwp, brand: effectiveBrand, type: effectiveType, residentialCash, is3p });
    if (!cashPrice) return null;

    // The rate shown is read back OUT of the price actually charged, never looked up a
    // second time: the old code re-read COMMERCIAL_PRICING, which has no Solis key at all
    // (crash ≥26 kWp) and a stale Huawei table that printed RM1,800 while the sale was
    // priced at RM1,850 (GoodWe String + RM100). One number, one source. (2026-09-26)
    const priceBreakdown = (() => {
        if (kwp <= 30 && effectiveBrand !== 'huawei') {
            const discountPerKwp = COMMERCIAL_PRICING[effectiveBrand]?.residentialDiscountPerKwp || 0;
            return {
                basis: 'residential',
                residentialCash,
                discountPerKwp,
                discountTotal: Math.round(discountPerKwp * kwp),
                finalPrice: cashPrice,
            };
        }
        const ratePerKwp = kwp > 0 ? Math.round(cashPrice / kwp) : 0;
        const tierLabel = kwp < 30 ? '26–30 kWp' : (kwp < 100 ? '30–<100 kWp' : (kwp <= 400 ? '100–400 kWp' : '>400 kWp'));
        return { basis: 'tier', ratePerKwp, tierLabel, finalPrice: cashPrice };
    })();

    let energy = mode === 'selco'
        ? estimateMonthlySavingSELCo({ kwp, tariffRate, monthlyKwh: breakdown.monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal, offHourBaselinePct })
        : estimateMonthlySaving({ kwp, tariffRate, monthlyKwh: breakdown.monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal, offHourBaselinePct });

    // "100% self-use" override — ignore export & curtailment: treat all generated
    // kWh as offsetting the bill, capped at actual consumption. Gives the
    // optimistic full-offset saving (final cap to the bill happens below).
    if (fullSelfUse) {
        // Scale by operating days: generation happens 7 days/week but is only self-used
        // on operating days (non-op days curtail — SELCo has no export). So 100% daytime
        // still loses (7 − operatingDays)/7 of the generation. This is why fewer operating
        // days now give a LOWER saving (user 2026-06-24) instead of a flat full-offset.
        const _opFrac = Math.min(1, (operatingDays || 7) / 7);
        const selfFull = Math.min(energy.generated * _opFrac, breakdown.monthlyKwh);
        energy = {
            ...energy,
            selfConsumed: selfFull,
            curtailed: 0,
            curtailedPct: 0,
            exported: 0,
            exportPct: 0,
            importAfterSolar: Math.max(0, breakdown.monthlyKwh - selfFull),
            monthlySaving: selfFull * tariffRate,
        };
    }

    // Right-size inverter to the candidate's actual kWp. Both ATAP and SELCo
    // can cross the 72 kW threshold (which triggers the ST License fee but
    // doesn't cap the system size). For ATAP the iteration is bounded by
    // breakdown.maxPanelsByInv (= MD × ratio); SELCo has no MD ceiling.
    const inverterKw = inverterKwOverride > 0 ? inverterKwOverride : inverterFromKwp(kwp, breakdown.ratioCap, invType);

    // If the inverter got capped into the license-free zone (72 kW) but this kWp
    // needs more, the ratio would exceed the cap — drop the candidate. This carves
    // out the 72→100 kW gap so nothing is proposed in that range (per user: cap at
    // 72 kW, jump straight to ≥100 kW for genuine large plants).
    if (inverterKwOverride <= 0 && kwp > inverterKw * breakdown.ratioCap + 0.001) return null;

    // ST License applies above 72 kW inverter — flat ~RM 800/month operating
    // expense that eats into the customer's net savings.
    const stLicenseApplies = inverterKw > ST_LICENSE_THRESHOLD_KW;
    // No ST license fee charged on break-free systems (user 2026-06-24) — flag kept
    // for display only; it never reduces the savings/ROI.
    const monthlyLicenseFee = 0;

    // Customer can never save more than 100% of their actual bill — TNB credits
    // for excess export top out at the bill total in practical terms (minimum
    // service charge ≈ RM 7 stays). Cap the gross saving first.
    const grossMonthlySaving = monthlyBill > 0
        ? Math.min(energy.monthlySaving, monthlyBill)
        : energy.monthlySaving;
    const savingCapped = grossMonthlySaving < energy.monthlySaving;

    // Net monthly saving = gross minus ST License fee (floor at 0). Used for
    // % of Bill Saved, ROI, and the long-horizon projections.
    const netMonthlySaving = Math.max(0, grossMonthlySaving - monthlyLicenseFee);

    const savingPct = monthlyBill > 0 ? netMonthlySaving / monthlyBill : 0;
    const roiYears = netMonthlySaving > 0 ? cashPrice / (netMonthlySaving * 12) : null;
    const roiYearsGross = grossMonthlySaving > 0 ? cashPrice / (grossMonthlySaving * 12) : null;

    return {
        kwp,
        panels,
        inverterKw,
        cashPrice,
        ratio: Math.round((kwp / inverterKw) * 100) / 100,
        savingPct,
        roiYears: roiYears != null ? Math.round(roiYears * 100) / 100 : null,
        roiYearsGross: roiYearsGross != null ? Math.round(roiYearsGross * 100) / 100 : null,
        brand: effectiveBrand,
        brandLabel: BRAND_LABEL[effectiveBrand],
        invType: effectiveType,
        brandType: effectiveType === 'hybrid' ? 'Hybrid' : 'String',
        // The '26–30' tier is gone with the band it named. (user 2026-10-14)
    priceTier: kwp <= 30 ? 'residential' : (kwp < 100 ? '<100' : (kwp < 400 ? '100–400' : '>400')),
        priceBreakdown,
        mode,
        savingCapped,
        stLicenseApplies,
        monthlyLicenseFee,
        grossMonthlySaving,
        ...energy,
        monthlySaving: netMonthlySaving,
    };
}

/**
 * Three-proposal CSC Bill picker.
 *
 * NEM mode — iterates panel counts from 1 to maxPanelsByInv (= MD × ratio cap):
 *   - maxSaving: highest monthly saving (typically MD-capped top end)
 *   - fastestRoi: lowest payback among candidates with savingPct ≥ 0.5
 *
 * SELCo mode — sized to match workday op-hour consumption (no MD cap, no
 * export revenue). Useful when MD constrains NEM but the customer wants more
 * coverage via the simpler zero-export approval path.
 *
 * Any of the three can be null:
 *   - fastestRoi: no candidate hits the 50% floor, or it would dedupe maxSaving
 *   - selco: no solar/op-hour overlap (24h ops with op outside the solar window)
 */
export default function useCscBillProposal({
    residentialRows,
    monthlyBill,
    tariffRate,
    operatingDays,
    opStart = 9,
    opEnd = 17,
    maxDemandKw,
    battKwhTotal = 0,
    phaseMode = 'single',
    mode = 'atap',
    brand = 'goodwe',
    invType = 'string',
    offHourBaselinePct = 0.2,
    fullSelfUse = false,
}) {
    return useMemo(() => {
        const breakdown = deriveSizing({ monthlyBill, tariffRate, operatingDays, maxDemandKw, battKwhTotal, phaseMode, mode, brand });
        if (!monthlyBill || !tariffRate || breakdown.maxPanelsByInv <= 0) {
            return { proposals: { maxSaving: null, fastestRoi: null, selco: null }, breakdown };
        }

        const is3p = phaseMode === '3phase';
        // Explicit master mode (user toggle): ATAP → NEM (export credited),
        // SELCo → zero export (self-consumption only).
        const primaryMode = mode === 'selco' ? 'selco' : 'nem';
        const candidates = [];

        // ATAP iteration honours MD only — 72 kW is a license threshold, not
        // a hard cap. Systems above 72 kW pay the ST License fee, deducted
        // from monthlySaving inside buildCandidate.
        for (let panels = 1; panels <= breakdown.maxPanelsByInv; panels++) {
            const c = buildCandidate({
                panels, brand, invType, is3p, residentialRows,
                monthlyBill, tariffRate, operatingDays, opStart, opEnd, battKwhTotal, breakdown, offHourBaselinePct, fullSelfUse,
                mode: primaryMode,
            });
            if (c) candidates.push(c);
        }

        // SELCo sizing: go to the MAX kWp the inverter supports (inverter × 1.35,
        // = breakdown.maxPanelsByInv). Without a battery the midday excess just
        // curtails; WITH a battery it's stored and used — so the max-kWp build is
        // what makes the battery worthwhile (per user 2026-06-02).
        const selcoOverlapHours = Math.max(0, Math.min(opEnd, SOLAR_END_HR) - Math.max(opStart, SOLAR_START_HR));
        let selco = null;
        if (selcoOverlapHours > 0 && breakdown.maxPanelsByInv > 0) {
            selco = buildCandidate({
                panels: breakdown.maxPanelsByInv, brand, invType, is3p, residentialRows,
                monthlyBill, tariffRate, operatingDays, opStart, opEnd, battKwhTotal, breakdown, offHourBaselinePct, fullSelfUse,
                mode: 'selco',
            });
        }

        if (candidates.length === 0) {
            return { proposals: { maxSaving: null, fastestRoi: null, selco }, breakdown };
        }

        // "Max" picks the SMALLEST INVERTER whose generation already covers the
        // customer's consumption (tie → fewest panels). The component then fills
        // panels to that inverter's max (<1.35 ratio). Choosing the smallest
        // inverter — not the smallest kWp — keeps the inverter from rounding up a
        // 5 kW step and ballooning the fill (e.g. RM350 ≈ 680 kWh → 5 kW / ~6.7 kWp,
        // not a 16 kW / 20 kWp monster).
        const monthlyKwhTarget = (Number(monthlyBill) || 0) / (Number(tariffRate) || 1);
        const covering = candidates.filter(c => (c.generated || 0) >= monthlyKwhTarget);
        // kWp needed to JUST cover consumption, and the inverter that size would
        // NATURALLY want (before the 72 kW cap). If that natural inverter lands in
        // the 72–100 kW dead zone, the customer doesn't truly need a 100 kW plant —
        // cap at the largest 72 kW (≈96 kWp) system instead of jumping to ~129 kWp.
        // (Per user: a 6k bill stays at 96 kWp; only ≥~134 kWp needs / 7k jumps to 100 kW.)
        const genPerKwp = candidates[0]?.kwp > 0 ? candidates[0].generated / candidates[0].kwp : 0;
        const requiredKwp = genPerKwp > 0 ? monthlyKwhTarget / genPerKwp : 0;
        const naturalInv = Math.max(5, Math.ceil(requiredKwp / RATIO_CAP_NO_BATT / 5) * 5);
        const max72 = candidates
            .filter(c => c.inverterKw <= ST_LICENSE_THRESHOLD_KW)
            .reduce((b, c) => (!b || c.kwp > b.kwp ? c : b), null);
        const pickCovering = () => covering.length
            ? covering.reduce((best, c) => (
                c.inverterKw < best.inverterKw ? c
                    : (c.inverterKw === best.inverterKw && c.panels < best.panels ? c : best)))
            : candidates.reduce((best, c) => (c.monthlySaving > best.monthlySaving ? c : best), candidates[0]);
        // Dead zone is now only 73–89 kW (break-free at 90): a natural inverter there
        // caps to the largest 72 kW system (~96 kWp); 90 kW+ breaks free to the real size.
        const maxSaving = (naturalInv > ST_LICENSE_THRESHOLD_KW && naturalInv < ST_BREAKFREE_KW && max72)
            ? max72
            : pickCovering();

        const roiPool = candidates.filter(c => c.savingPct >= 0.5 && c.roiYears != null);
        let fastestRoi = roiPool.length > 0
            ? roiPool.reduce((best, c) => c.roiYears < best.roiYears ? c : best, roiPool[0])
            : null;
        if (fastestRoi && fastestRoi.panels === maxSaving.panels) fastestRoi = null;

        // 50% Saved card: smallest panel count whose savingPct ≥ 0.5. Sized
        // to the budget-conscious "what's the cheapest ATAP that still covers
        // half my bill" question. Often coincides with Fastest ROI in the
        // common linear-saving regime — dedupe so we don't show twins.
        let targetFifty = roiPool.length > 0 ? roiPool[0] : null;
        if (targetFifty && (targetFifty.panels === maxSaving.panels || targetFifty.panels === fastestRoi?.panels)) {
            targetFifty = null;
        }

        // "Snap to 96 kWp" package — 96 kWp on a 72 kW inverter (ratio 1.33, just
        // UNDER the 72 kW ST-license threshold → ST-free). Only offered when the
        // Max Demand can carry a 72 kW inverter (MD ≥ 72). Callers snap a near-96
        // card (kWp ≈ 88–96) to this standard ST-free package.
        let snap96 = null;
        if (maxDemandKw >= 72) {
            snap96 = buildCandidate({
                panels: Math.round(96 / PANEL_KWP), inverterKwOverride: 72,
                brand, invType, is3p, residentialRows,
                monthlyBill, tariffRate, operatingDays, opStart, opEnd, battKwhTotal, breakdown, offHourBaselinePct, fullSelfUse,
                mode: 'nem',
            });
        }

        // Expose the full NEM candidate list so callers can size to an arbitrary
        // bill-saving target (e.g. 50% / 70%). Additive — existing consumers ignore it.
        return { proposals: { maxSaving, fastestRoi, targetFifty, selco }, candidates, snap96, breakdown };
    }, [residentialRows, monthlyBill, tariffRate, operatingDays, opStart, opEnd, maxDemandKw, battKwhTotal, phaseMode, mode, brand, invType, offHourBaselinePct, fullSelfUse]);
}
